import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { PROFANITY_LIST } from './wroe-profanity-list.js';

/**
 * dm-cart-drawer — the cart drawer mechanics handoff 286:59 has no existing owner for.
 *
 * Phase B/C of the handoff, all DRAWER ONLY (scope ruling 2026-09-01 — the cart page is
 * out of scope and must not pick any of this up):
 *
 *   State 5  Swipe to delete      — coarse pointers only; a mouse has the trash button.
 *   State 6  Undo toast           — the removed line's payload is captured from the row
 *                                   BEFORE removal, because a removed line cannot be
 *                                   read back out of the cart.
 *   State 10 Edit personalization — Shopify line properties are IMMUTABLE, so saving
 *                                   ADDS the edited line first and only then removes the
 *                                   original by key: a failed add changes nothing, so the
 *                                   customer can never lose the item. Quantity and any
 *                                   Rush add-on line are carried across explicitly.
 *   State 12 Checkout loading     — fades the items and marks the CTA busy.
 *   Rush production              — a SEPARATE cart line (user ruling 2026-09-01), gated
 *                                   on a configured variant ID so it can never post a
 *                                   variant that does not exist.
 *   State 1  Recently viewed      — the visitor's own history from localStorage. Nothing
 *                                   is tracked server-side. A personalized product never
 *                                   gets Add (it would post a line with no Pet Name); it
 *                                   links to its page as "Personalize" instead. A
 *                                   product with options to choose (size, colour)
 *                                   links as "Choose options"; only a single-variant
 *                                   product gets Quick Add.
 *
 * The edit modal is a <dialog> nested inside the drawer's <dialog>. Escape and the
 * native close request dismiss ONLY the modal (dialog.js would otherwise close the drawer
 * underneath it and leave an invisible modal that makes the whole page inert), and the
 * modal is closed whenever the drawer closes so it can never outlive its parent.
 *
 * The drawer <dialog> sits outside the morphed .cart-drawer__inner, so after any cart
 * change this module re-syncs its `cart-drawer--empty` modifier and reports the real
 * `item_count` to the header bubble (cart-icon.js treats a missing count as 0).
 *
 * Motion is under-specified in the handoff (its only annotation is the promo shake), so
 * timings follow design.pen §18 rather than being invented.
 */
const TOAST_HOLD_MS = 5000;
// Figma 240:7710 / 240:7826: the toast's bottom edge sits 35px above the Checkout CTA
// (240:7822), over the totals rows and never over the CTA. With no CTA (the removal
// emptied the cart) it drops to the dialog edge.
const TOAST_CTA_GAP = 35;
const TOAST_EDGE = 16;
const TOAST_HEIGHT = 48;
const SWIPE_COMMIT_PX = 96;
const HISTORY_KEY = 'dm-recently-viewed';
// Handles seen on a personalized PDP (fix-plan F1-2). /products/<handle>.js carries no
// template suffix, so the page records it while it still knows.
const PERSONALIZED_KEY = 'dm-recent-personalized';
const PERSONALIZED_TAG = /customi[sz]able|personali[sz]ed/i;
// Short name per handle, recorded on the product page for the same reason: /products/<handle>.js
// carries no metafields. A product with no entry shows its title.
const SHORT_NAMES_KEY = 'dm-recent-short-names';
const HISTORY_MAX = 8;
const RECENT_MAX = 3;
// Matches the configurator's default (dm-personalized.md §Name validation). A line that
// was added under a longer per-product limit keeps its own length as the ceiling.
const NAME_MAX_DEFAULT = 10;
// Line controls focus can be rebuilt onto after a cart morph (focus rescue), matched in
// this order. A rescue only ever lands on the same kind of control on the same line.
const LINE_CONTROLS = [
  '.quantity-minus',
  '.quantity-plus',
  'input[type="number"]',
  '[data-dm-edit-line]',
  '[data-dm-rush]',
  '.cart-items__title',
  '.cart-items__media-container',
];

class DmCartDrawer extends Component {
  requiredRefs = ['toast', 'undo'];

  /** @type {{ id: number, quantity: number, title: string, properties: Record<string,string>, selling_plan?: number } | null} */

  #pruningRush = false;
  #lastRemoved = null;

  #toastTimer = 0;
  #busy = false;

  /** Focus was on the removed line (or its trash) when it went; the Undo button takes it. */
  #removedHadFocus = false;

  /** 1-based index of the removed line; its successor takes focus if the toast expires. */
  #removedIndex = 0;

  /**
   * The line control that last held focus inside the drawer. A cart morph (or Horizon
   * disabling − at the minimum quantity) can drop that node and leave focus on <body>
   * while the drawer is still the open modal; this is what focus is rebuilt from.
   *
   * @type {{ key: string, index: number, kind: string } | null}
   */
  #lastLineFocus = null;

  #rescueFrame = 0;

  /** @type {HTMLDialogElement | null} the drawer's own dialog, which this element sits in */
  #drawerDialog = null;

  /** @type {HTMLElement | null} cart-drawer-component, which dispatches dialog:open */
  #drawerComponent = null;

  /** @type {MutationObserver | null} */
  #contentObserver = null;

  #syncFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#onClick, true);
    document.addEventListener('pointerdown', this.#onPointerDown);
    this.refs.undo?.addEventListener('click', this.#onUndo);
    this.refs.modalSave?.addEventListener('click', this.#onModalSave);
    this.refs.modalCancel?.addEventListener('click', this.#closeModal);
    this.refs.modalClose?.addEventListener('click', this.#closeModal);
    this.refs.modalName?.addEventListener('input', this.#onNameInput);
    // Enter in the name field submits the form; with method="dialog" that would close the
    // modal and silently drop the edit. Treat it as Save instead.
    this.refs.modalForm?.addEventListener('submit', this.#onModalSubmit);

    // Escape / close requests on the nested modal must stop at the modal (P0, F1-1).
    this.refs.modal?.addEventListener('keydown', this.#onModalKeyDown);
    this.refs.modal?.addEventListener('cancel', this.#onModalCancel);
    this.refs.modal?.addEventListener('click', this.#onModalBackdrop);
    this.refs.modalEmbroidery?.addEventListener('change', this.#onSelectChange);
    this.refs.modalToy?.addEventListener('change', this.#onSelectChange);

    this.#drawerDialog = this.closest('dialog');
    this.#drawerDialog?.addEventListener('close', this.#onDrawerClose);
    // Focus rescue after cart morphs (qa-cart-r1-focus-lost-after-drawer-actions).
    this.#drawerDialog?.addEventListener('focusin', this.#onFocusIn);
    document.addEventListener('cart:update', this.#queueFocusRescue);

    // The empty-state list is appended inside the morphed subtree, so a hydration morph can
    // drop it. Opening the drawer re-renders it (idempotent: it bails if already present).
    this.#drawerComponent = this.closest('cart-drawer-component');
    this.#drawerComponent?.addEventListener('dialog:open', this.#renderRecentlyViewed);

    // Every cart path (theme product form, cart-items, this module) morphs only the inner
    // subtree; keep the dialog-level empty modifier in step with what was rendered. The
    // dialog itself is observed because it is the one node no morph replaces.
    if (this.#drawerDialog) {
      this.#contentObserver = new MutationObserver(this.#queueEmptySync);
      this.#contentObserver.observe(this.#drawerDialog, { childList: true, subtree: true });
    }

    this.#recordHistory();
    this.#renderRecentlyViewed();
    document.addEventListener('cart:update', this.#renderRecentlyViewed);
    document.addEventListener('cart:update', this.#pruneOrphanRush);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:update', this.#pruneOrphanRush);
    document.removeEventListener('click', this.#onClick, true);
    document.removeEventListener('pointerdown', this.#onPointerDown);
    document.removeEventListener('cart:update', this.#renderRecentlyViewed);
    document.removeEventListener('cart:update', this.#queueFocusRescue);
    this.#drawerDialog?.removeEventListener('close', this.#onDrawerClose);
    this.#drawerDialog?.removeEventListener('focusin', this.#onFocusIn);
    window.cancelAnimationFrame(this.#rescueFrame);
    this.#drawerComponent?.removeEventListener('dialog:open', this.#renderRecentlyViewed);
    this.#contentObserver?.disconnect();
    this.#contentObserver = null;
    window.cancelAnimationFrame(this.#syncFrame);
    this.#disarmToastTriggers();
    window.clearTimeout(this.#toastTimer);
    window.removeEventListener('resize', this.#positionToast);
  }

  // ------------------------------------------------------------------ routing

  #onClick = (/** @type {Event} */ event) => {
    // Element, not HTMLElement: a tap on the pencil, trash or lock glyph targets the
    // <svg>/<path> inside the control (an SVGElement), and closest() works from there too.
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Capture the payload on the way DOWN, before the theme's own remove handler runs
    // and the row is gone. This listener is registered in the capture phase for that.
    const remove = target.closest('.cart-items__remove, .cart-items__remove-confirm-button');
    if (remove) {
      this.#stashLine(remove.closest('[data-dm-line]'), remove);
      return;
    }

    const edit = target.closest('[data-dm-edit-line]');
    if (edit instanceof HTMLElement) {
      event.preventDefault();
      this.#openModal(edit.closest('[data-dm-line]'), edit);
      return;
    }

    const rush = target.closest('[data-dm-rush]');
    if (rush instanceof HTMLElement) {
      event.preventDefault();
      this.#toggleRush(rush);
      return;
    }

    const checkout = target.closest('[name="checkout"]');
    if (checkout) this.#setCheckoutLoading(true);

    const add = target.closest('[data-dm-recent-add]');
    if (add instanceof HTMLElement) {
      event.preventDefault();
      this.#addRecent(add);
    }
  };

  /**
   * @param {Element | null} row
   * @param {Element | null} [control] the remove control that was pressed, if any
   */
  #stashLine(row, control = null) {
    if (!(row instanceof HTMLElement)) return;
    try {
      this.#lastRemoved = this.#readLine(row);
      // Where focus was when the line went: the toast takes it over only if the customer
      // was on that line (keyboard, or a click that focused the trash), never from
      // somewhere else in the drawer.
      const active = document.activeElement;
      this.#removedHadFocus = !!active && active !== document.body && (row.contains(active) || control === active);
      this.#removedIndex = Number(row.dataset.dmIndex) || 0;
      // A removal is only worth undoing once the cart actually changes, so the toast is
      // raised on the next cart update rather than on the click. Either signal can arrive
      // first depending on whether the theme removed the line or this module did, so both
      // are armed together and BOTH are disarmed by whichever fires — `{ once: true }`
      // alone would leave the loser registered and pop a stale toast later.
      document.addEventListener('cart:update', this.#showUndoToast);
      this.addEventListener('dm:cart-changed', this.#showUndoToast);
    } catch {
      this.#lastRemoved = null;
    }
  }

  /**
   * Parse a row's line payload (cart-products.liquid `data-dm-line`). Liquid hands line
   * property values over HTML-escaped ("…&amp;width=120"); re-posting them as-is on Undo,
   * Edit or a Rush move would write the escaped text into the new line. Decode them back
   * to the values the cart actually holds.
   *
   * @param {Element | null} row
   * @returns {any} the payload, or null when absent or unreadable
   */
  #readLine(row) {
    if (!(row instanceof HTMLElement)) return null;
    let line;
    try {
      line = JSON.parse(row.dataset.dmLine || 'null');
    } catch {
      return null;
    }
    if (!line || typeof line !== 'object') return null;
    if (typeof line.title === 'string') line.title = this.#decode(line.title);
    if (line.properties && typeof line.properties === 'object') {
      for (const [key, value] of Object.entries(line.properties)) {
        if (typeof value === 'string') line.properties[key] = this.#decode(value);
      }
    }
    return line;
  }

  /** @param {string} value */
  #decode(value) {
    if (!value.includes('&')) return value;
    const scratch = document.createElement('textarea');
    scratch.innerHTML = value;
    return scratch.value;
  }

  // -------------------------------------------------------------- undo toast

  #showUndoToast = () => {
    this.#disarmToastTriggers();

    const toast = this.refs.toast;
    const text = this.refs.toastText;
    if (!(toast instanceof HTMLElement) || !this.#lastRemoved) return;

    if (text instanceof HTMLElement) {
      text.textContent = `${this.#lastRemoved.title || 'Item'} removed`;
    }

    toast.hidden = false;
    // Anchor above the CTA before the first painted frame, then track it while shown:
    // the theme dispatches cart:update before it morphs, so the summary can still move
    // (the content observer re-runs this after the morph lands).
    this.#positionToast();
    window.addEventListener('resize', this.#positionToast);
    requestAnimationFrame(() => (toast.dataset.visible = 'true'));

    // The trash (or the whole row) that held focus is gone or going; without this focus
    // falls to <body> inside the open modal drawer. Hand it to Undo, the one control that
    // reverses what just happened.
    const undo = this.refs.undo;
    if (undo instanceof HTMLElement && this.#drawerDialog?.open && (this.#removedHadFocus || this.#focusLost())) {
      undo.focus({ preventScroll: true });
    }
    this.#removedHadFocus = false;

    window.clearTimeout(this.#toastTimer);
    this.#toastTimer = window.setTimeout(() => this.#hideToast(), TOAST_HOLD_MS);
  };

  #disarmToastTriggers() {
    document.removeEventListener('cart:update', this.#showUndoToast);
    this.removeEventListener('dm:cart-changed', this.#showUndoToast);
  }

  /**
   * Sets --dm-toast-bottom (read by `.dm-cart-toast` in dm-cart-drawer-extras.liquid) so
   * the toast's bottom edge sits TOAST_CTA_GAP above the drawer's Checkout button. Falls
   * back to TOAST_EDGE when there is no visible CTA inside the dialog (empty cart, or a
   * non-sticky summary scrolled out of view), and never lets the toast leave the dialog.
   */
  #positionToast = () => {
    const toast = this.refs.toast;
    const dialog = this.#drawerDialog;
    if (!(toast instanceof HTMLElement) || !dialog || toast.hidden) return;

    const box = dialog.getBoundingClientRect();
    let bottom = TOAST_EDGE;
    for (const checkout of dialog.querySelectorAll('[name="checkout"]')) {
      const rect = checkout.getBoundingClientRect();
      if (!rect.height || rect.top < box.top || rect.top >= box.bottom) continue;
      const ceiling = Math.max(TOAST_EDGE, box.height - (toast.offsetHeight || TOAST_HEIGHT) - TOAST_EDGE);
      bottom = Math.min(Math.max(box.bottom - rect.top + TOAST_CTA_GAP, TOAST_EDGE), ceiling);
      break;
    }

    const value = `${Math.round(bottom)}px`;
    if (toast.style.getPropertyValue('--dm-toast-bottom') !== value) {
      toast.style.setProperty('--dm-toast-bottom', value);
    }
  };

  /**
   * @param {{ restoreFocus?: boolean }} [options]
   *   restoreFocus: when the toast expires while Undo holds focus, move focus to the line
   *   that took the removed line's place (or the drawer's close button) before hiding it.
   *   Undo itself passes false: it focuses the restored line once the add resolves.
   */
  #hideToast({ restoreFocus = true } = {}) {
    const toast = this.refs.toast;
    if (!(toast instanceof HTMLElement)) return;
    toast.dataset.visible = 'false';
    const index = this.#removedIndex;
    window.setTimeout(() => {
      if (toast.dataset.visible === 'true') return;
      if (restoreFocus && toast.contains(document.activeElement)) {
        this.#focusLineTarget(this.#lineAt(index));
      }
      toast.hidden = true;
      window.removeEventListener('resize', this.#positionToast);
    }, 300);
    this.#lastRemoved = null;
  }

  #onUndo = async () => {
    const line = this.#lastRemoved;
    if (!line || this.#busy) return;

    this.#hideToast({ restoreFocus: false });
    const added = await this.#addItems([
      {
        id: line.id,
        quantity: line.quantity,
        properties: line.properties || {},
        ...(line.selling_plan ? { selling_plan: line.selling_plan } : {}),
      },
    ]);

    // Undo (inside the toast) is hidden now; put focus on the line it brought back.
    const row = await this.#waitForLine(added?.items?.[0]?.key);
    if (this.#focusLost(this.refs.toast)) this.#focusLineTarget(row);
  };

  // ------------------------------------------------------------------- focus

  /**
   * Focus has nowhere live to be: on <body>, on a node a morph removed, on a disabled or
   * hidden control, or still inside one of `within` (a control that is going away).
   *
   * @param {...(unknown)} within
   */
  #focusLost(...within) {
    const active = document.activeElement;
    if (!active || active === document.body || !active.isConnected) return true;
    if (active.matches(':disabled') || active.closest('[hidden]')) return true;
    return within.some((element) => element instanceof Element && element.contains(active));
  }

  /** @param {string | undefined} key */
  #lineByKey(key) {
    if (!key || !this.#drawerDialog) return null;
    return this.#drawerDialog.querySelector(`[data-dm-line][data-key="${CSS.escape(key)}"]`);
  }

  /**
   * The line with this key once it is rendered. Usually it already is (the add response
   * carried the drawer section); when the section had to be re-fetched it lands a moment
   * later, so wait for it briefly rather than giving up on it.
   *
   * @param {string | undefined} key
   * @param {number} [timeout]
   * @returns {Promise<Element | null>}
   */
  #waitForLine(key, timeout = 2500) {
    const dialog = this.#drawerDialog;
    const now = this.#lineByKey(key);
    if (now || !key || !dialog) return Promise.resolve(now);
    return new Promise((resolve) => {
      /** @param {Element | null} row */
      const done = (row) => {
        observer.disconnect();
        window.clearTimeout(timer);
        resolve(row);
      };
      const observer = new MutationObserver(() => {
        const row = this.#lineByKey(key);
        if (row) done(row);
      });
      const timer = window.setTimeout(() => done(null), timeout);
      observer.observe(dialog, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-key'] });
    });
  }

  /**
   * The line now at a 1-based position, or the last line when the list got shorter.
   *
   * @param {number} index
   */
  #lineAt(index) {
    const rows = this.#drawerDialog ? [...this.#drawerDialog.querySelectorAll('[data-dm-line]')] : [];
    if (!rows.length) return null;
    return rows[Math.min(Math.max(index, 1), rows.length) - 1] || null;
  }

  /**
   * Focus a line's own entry point (Edit when it has one, else its title link), falling
   * back to the drawer's close button, the same targets #dismissModal resolves.
   *
   * @param {Element | null} row
   * @param {string[]} [prefer] selectors tried first inside the row
   */
  #focusLineTarget(row, prefer = []) {
    const dialog = this.#drawerDialog;
    if (!dialog?.open) return;
    const modal = this.refs.modal;
    if (modal instanceof HTMLDialogElement && modal.open) return;

    /** @type {Element | null} */
    let target = null;
    if (row) {
      for (const selector of [...prefer, '[data-dm-edit-line]', '.cart-items__title']) {
        const candidate = selector ? row.querySelector(selector) : null;
        if (candidate instanceof HTMLElement && !candidate.matches(':disabled') && candidate.getClientRects().length) {
          target = candidate;
          break;
        }
      }
    }
    target ||= dialog.querySelector('.cart-drawer__close-button');
    if (target instanceof HTMLElement) target.focus({ preventScroll: !row });
  }

  #onFocusIn = (/** @type {FocusEvent} */ event) => {
    const target = event.target;
    const row = target instanceof Element ? target.closest('[data-dm-line]') : null;
    if (!(row instanceof HTMLElement) || !(target instanceof Element)) {
      this.#lastLineFocus = null;
      return;
    }
    this.#lastLineFocus = {
      key: row.dataset.key || '',
      index: Number(row.dataset.dmIndex) || 0,
      kind: LINE_CONTROLS.find((selector) => target.matches(selector)) || '',
    };
  };

  /**
   * After any cart update (Horizon's quantity steppers included), put focus back on the
   * same control of the same line if a morph or a disabled − button dropped it. Runs a
   * frame later so the morph, Horizon's button-state pass and this module's own explicit
   * focus moves (Undo, toast, Recently viewed, modal save) have all happened first; it
   * only acts when focus is still lost.
   */
  #queueFocusRescue = () => {
    window.cancelAnimationFrame(this.#rescueFrame);
    this.#rescueFrame = window.requestAnimationFrame(() => {
      const memo = this.#lastLineFocus;
      if (!memo || !this.#drawerDialog?.open || !this.#focusLost()) return;
      const row = this.#lineByKey(memo.key) || this.#lineAt(memo.index);
      // − at the minimum quantity is disabled: the quantity field beside it is next.
      const prefer = [memo.kind, ...(memo.kind === '.quantity-minus' ? ['input[type="number"]', '.quantity-plus'] : [])];
      this.#focusLineTarget(row, prefer);
    });
  };

  // ----------------------------------------------------------------- swiping

  /** @type {{ row: HTMLElement, x: number, y: number, id: number } | null} */
  #swipe = null;

  #onPointerDown = (/** @type {PointerEvent} */ event) => {
    // Coarse pointers only. A trackpad drag would otherwise delete lines by accident.
    if (event.pointerType === 'mouse') return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    const row = target.closest('.cart-items__wrapper--drawer-surface [data-dm-line]');
    if (!(row instanceof HTMLElement)) return;
    // Never start a swipe from a control the customer meant to press. The title link is
    // the exception: its 44px hit area now spans the badge and variant line (where swipes
    // used to start), and a drag never becomes a click, so a tap still opens the product.
    if (target.closest('button, input, select, a:not(.cart-items__title)')) return;

    this.#swipe = { row, x: event.clientX, y: event.clientY, id: event.pointerId };
    row.dataset.dmSwipe = 'active';
    document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    document.addEventListener('pointerup', this.#onPointerUp);
    document.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (/** @type {PointerEvent} */ event) => {
    const swipe = this.#swipe;
    if (!swipe || event.pointerId !== swipe.id) return;

    const dx = event.clientX - swipe.x;
    const dy = event.clientY - swipe.y;

    // A mostly-vertical drag is the customer scrolling the drawer, not swiping a line.
    if (Math.abs(dy) > Math.abs(dx)) {
      this.#endSwipe(false);
      return;
    }

    const travel = Math.min(0, dx);
    swipe.row.style.setProperty('--dm-swipe-x', `${travel}px`);
    swipe.row.style.setProperty('--dm-swipe-reveal', `${Math.abs(travel)}px`);
  };

  #onPointerUp = (/** @type {PointerEvent} */ event) => {
    const swipe = this.#swipe;
    if (!swipe || event.pointerId !== swipe.id) return;

    const travel = Math.abs(parseFloat(swipe.row.style.getPropertyValue('--dm-swipe-x')) || 0);
    this.#endSwipe(travel >= SWIPE_COMMIT_PX);
  };

  /** @param {boolean} commit */
  #endSwipe(commit) {
    const swipe = this.#swipe;
    this.#swipe = null;
    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerup', this.#onPointerUp);
    document.removeEventListener('pointercancel', this.#onPointerUp);
    if (!swipe) return;

    const { row } = swipe;
    row.dataset.dmSwipe = 'settling';
    row.style.setProperty('--dm-swipe-x', '0px');
    row.style.setProperty('--dm-swipe-reveal', '0px');
    window.setTimeout(() => delete row.dataset.dmSwipe, 260);

    if (commit) {
      this.#stashLine(row);
      this.#changeLine(row.dataset.dmIndex, 0);
    }
  }

  // ------------------------------------------------------------- edit modal

  /** @type {HTMLElement | null} */
  #editingRow = null;

  /** @type {any} */
  #editingLine = null;

  /** @type {HTMLElement | null} the line's Edit button; focus returns here on close */
  #modalTrigger = null;

  /**
   * @param {Element | null} row
   * @param {HTMLElement | null} [trigger]
   */
  #openModal(row, trigger = null) {
    const modal = this.refs.modal;
    if (!(row instanceof HTMLElement) || !(modal instanceof HTMLDialogElement)) return;
    if (modal.open) return;

    /** @type {any} */
    const line = this.#readLine(row);
    if (!line) return;

    this.#editingRow = row;
    this.#editingLine = line;
    this.#modalTrigger = trigger;

    const product = this.refs.modalProduct;
    if (product instanceof HTMLElement) product.textContent = line.title || '';

    const thumb = this.refs.modalThumb;
    if (thumb instanceof HTMLImageElement) {
      thumb.hidden = !line.image;
      if (line.image) thumb.src = line.image;
    }

    const name = this.refs.modalName;
    if (name instanceof HTMLInputElement) {
      name.value = line.properties?.['Pet Name'] || '';
      name.maxLength = this.#nameMax(line);
    }

    const nameLabel = this.refs.modalNameLabel;
    if (nameLabel instanceof HTMLElement) nameLabel.textContent = line.nameInitials ? 'Initials' : 'Pet name';

    // The frame labels these PRIMARY / ACCENT COLOR. Display labels may differ; the
    // property KEYS (Embroidery Color / Toy Color) are the live contract and never change.
    this.#fillSelect(
      this.refs.modalEmbroidery,
      this.refs.modalEmbroideryField,
      line.embroideryOptions,
      line.properties?.['Embroidery Color'],
      line.properties?.['_wroe_embroidery_color_swatch'],
      'raw'
    );
    const fabric = this.#fabricKeys(line);
    this.#fillSelect(
      this.refs.modalToy,
      this.refs.modalToyField,
      line.toyOptions,
      line.properties?.[fabric.key],
      line.properties?.[fabric.swatch],
      'display'
    );

    this.#setModalError('');
    modal.showModal();
    if (name instanceof HTMLInputElement) name.focus();
  }

  /**
   * Hub labels arrive as merchants typed them ('purple', 'skyblue'). The option TEXT is
   * word-capitalised (Figma 240:8263) with the configurator's own rule. The option VALUE
   * is what the product page posts for that property, so an edited line carries the same
   * value a fresh add would: the configurator posts Toy Color as its word-capitalised
   * display label (dm-configurator-step-2 data-label) and Embroidery Color as the raw ALT
   * label. The line's own option keeps the line's exact stored value, so a field the
   * customer did not touch never rewrites its property (or its swatch).
   *
   * @param {unknown} select
   * @param {unknown} field
   * @param {{ label: string, swatch: string }[] | undefined} options
   * @param {string | undefined} current
   * @param {string | undefined} [currentSwatch]
   * @param {'raw' | 'display'} [valueAs]
   */
  #fillSelect(select, field, options, current, currentSwatch = '', valueAs = 'raw') {
    if (!(select instanceof HTMLSelectElement) || !(field instanceof HTMLElement)) return;

    select.replaceChildren();
    const list = Array.isArray(options) ? options : [];
    // A line without a current value for this property never had the choice; don't offer it.
    field.hidden = list.length === 0 || !current;
    if (field.hidden) {
      this.#syncDot(select);
      return;
    }

    const currentKey = this.#labelKey(current);
    let matched = false;

    for (const option of list) {
      const raw = String(option?.label ?? '').trim();
      if (!raw) continue;
      const isCurrent = !matched && this.#labelKey(raw) === currentKey;
      const value = isCurrent ? String(current) : valueAs === 'display' ? this.#displayLabel(raw) : raw;
      const element = new Option(this.#displayLabel(raw), value, isCurrent, isCurrent);
      element.dataset.swatch = option.swatch || '';
      select.append(element);
      if (isCurrent) matched = true;
    }

    // Keep the current value selectable even if the merchant has since retired it.
    if (current && !matched) {
      const element = new Option(this.#displayLabel(current), current, true, true);
      element.dataset.swatch = this.#safeUrl(currentSwatch);
      select.prepend(element);
    }

    this.#syncDot(select);
  }

  /** @param {unknown} value */
  #labelKey(value) {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  /**
   * Word-capitalise a label exactly as the configurator does (dm-configurator-step-2 /
   * dm-preview-config: first letter of each word upcased, the rest left as typed), so the
   * drawer and the product page print and post the same name for the same hub label.
   *
   * @param {unknown} value
   */
  #displayLabel(value) {
    return String(value ?? '')
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Only Shopify CDN images (or same-origin paths) ever reach a style value.
   *
   * @param {unknown} value
   */
  #safeUrl(value) {
    const url = String(value ?? '').replace(/&amp;/g, '&');
    if (!url || /["'()\\\s]/.test(url)) return '';
    return /^(https:)?\/\/cdn\.shopify\.com\/|^\/(?!\/)|\/cdn\/shop\//.test(url) ? url : '';
  }

  #onSelectChange = (/** @type {Event} */ event) => {
    if (event.target instanceof HTMLSelectElement) this.#syncDot(event.target);
  };

  /**
   * The 12px colour dot beside a select (Figma 240:8263). Its fill is the option's swatch
   * image cropped to texture (fix-plan §2.2 fill source 3), never the whole scene.
   *
   * @param {HTMLSelectElement} select
   */
  #syncDot(select) {
    const dot = select.parentElement?.querySelector('.dm-cart-modal__dot');
    if (!(dot instanceof HTMLElement)) return;
    const url = this.#safeUrl(select.selectedOptions[0]?.dataset.swatch);
    dot.hidden = !url;
    if (url) dot.style.setProperty('--dm-swatch-image', `url("${url}")`);
    else dot.style.removeProperty('--dm-swatch-image');
  }

  /** @param {any} line */
  #nameMax(line) {
    if (line?.nameInitials) return line.nameInitials;
    return Math.max(NAME_MAX_DEFAULT, String(line?.properties?.['Pet Name'] || '').length);
  }

  #onNameInput = (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const cleaned = input.value.toUpperCase().replace(/[^A-Z]/g, '');
    if (cleaned !== input.value) input.value = cleaned;
  };

  /** @param {string} value */
  #isProfane(value) {
    const normalized = value.toLowerCase();
    if (PROFANITY_LIST.has(normalized)) return true;
    for (const term of PROFANITY_LIST) {
      if (term.length >= 4 && normalized.includes(term)) return true;
    }
    return false;
  }

  /** Cancel, the close button, Escape and the backdrop all land here. */
  #closeModal = () => {
    this.#dismissModal({ restoreFocus: true });
  };

  /**
   * @param {{ restoreFocus?: boolean, focusKey?: string }} [options]
   *   focusKey: after a save the edited line has a new key (and a new Edit button), so
   *   focus goes to that line's Edit button rather than to a node the morph removed.
   */
  #dismissModal({ restoreFocus = false, focusKey = '' } = {}) {
    const modal = this.refs.modal;
    const trigger = this.#modalTrigger;
    const save = this.refs.modalSave;

    this.#modalTrigger = null;
    this.#editingRow = null;
    this.#editingLine = null;
    if (save instanceof HTMLElement) save.removeAttribute('aria-busy');
    if (modal instanceof HTMLDialogElement && modal.open) modal.close();

    if (!restoreFocus || !this.#drawerDialog?.open) return;

    /** @type {Element | null | undefined} */
    let target = null;
    if (focusKey) {
      target = this.#drawerDialog.querySelector(`[data-key="${CSS.escape(focusKey)}"] [data-dm-edit-line]`);
    }
    if (!target && trigger?.isConnected) target = trigger;
    if (!target) target = this.#drawerDialog.querySelector('.cart-drawer__close-button');
    if (target instanceof HTMLElement) target.focus({ preventScroll: !focusKey });
  }

  /**
   * Escape on the nested modal. dialog.js listens for keydown on cart-drawer-component and
   * would preventDefault (cancelling the modal's own close) and close the DRAWER, leaving
   * an open, zero-size modal that makes the page inert. Stop it here.
   */
  #onModalKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    event.preventDefault();
    this.#closeModal();
  };

  #onModalSubmit = (/** @type {SubmitEvent} */ event) => {
    event.preventDefault();
    this.#onModalSave();
  };

  /** Native close request (Escape where not intercepted, Android back). */
  #onModalCancel = (/** @type {Event} */ event) => {
    event.preventDefault();
    this.#closeModal();
  };

  /**
   * A click on the modal's ::backdrop targets the dialog itself. Dismiss the modal only,
   * and keep the click from reaching dialog.js's outside-click handler on the drawer.
   */
  #onModalBackdrop = (/** @type {MouseEvent} */ event) => {
    const modal = this.refs.modal;
    if (!(modal instanceof HTMLDialogElement) || event.target !== modal) return;
    const box = modal.getBoundingClientRect();
    const inside =
      event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (inside) return;
    event.stopPropagation();
    this.#closeModal();
  };

  /** The modal must never outlive the drawer it belongs to. */
  #onDrawerClose = (/** @type {Event} */ event) => {
    if (event.target !== this.#drawerDialog) return;
    const modal = this.refs.modal;
    const wasOpen = modal instanceof HTMLDialogElement && modal.open;
    this.#dismissModal({ restoreFocus: false });
    if (!wasOpen) return;

    // The drawer tried to hand focus back to its opener while the modal still held the
    // page inert, so focus fell to <body>. Put it on the header Cart button instead.
    const active = document.activeElement;
    if (!active || active === document.body || this.#drawerDialog?.contains(active)) {
      const opener = this.#drawerComponent?.querySelector('[aria-haspopup="dialog"]');
      if (opener instanceof HTMLElement) opener.focus({ preventScroll: true });
    }
  };

  #onModalSave = async () => {
    const line = this.#editingLine;
    const name = this.refs.modalName;
    const save = this.refs.modalSave;
    if (!line || !(name instanceof HTMLInputElement) || this.#busy) return;

    // Same rules as the configurator: required, letters only, length cap, no profanity.
    const next = name.value.trim().toUpperCase();
    if (line.nameInitials && next.length !== line.nameInitials) {
      return this.#setModalError(`Enter ${line.nameInitials} letters, like NN.`);
    }
    if (!next) return this.#setModalError('Enter a name.');
    if (!/^[A-Z]+$/.test(next)) return this.#setModalError('Letters only, please.');
    if (next.length > this.#nameMax(line)) {
      return this.#setModalError(`Names can be up to ${this.#nameMax(line)} letters.`);
    }
    if (this.#isProfane(next)) {
      return this.#setModalError(line.nameInitials ? 'Please choose different initials.' : 'Please choose a different name.');
    }

    const properties = { ...(line.properties || {}), 'Pet Name': next };
    this.#applyChoice(properties, this.refs.modalEmbroidery, 'Embroidery Color', '_wroe_embroidery_color_swatch');
    const fabric = this.#fabricKeys(line);
    this.#applyChoice(properties, this.refs.modalToy, fabric.key, fabric.swatch);

    const before = line.properties || {};
    const unchanged =
      Object.keys(properties).every((key) => properties[key] === before[key]) &&
      Object.keys(before).every((key) => key in properties);
    if (unchanged) {
      this.#closeModal();
      return;
    }

    if (save instanceof HTMLElement) save.setAttribute('aria-busy', 'true');
    this.#setModalError('');

    const rushRow = this.#findRushRow(line.key);

    // 1. Add the edited line FIRST. If this fails nothing has changed.
    const added = await this.#addItems([
      {
        id: line.id,
        quantity: line.quantity,
        properties,
        ...(line.selling_plan ? { selling_plan: line.selling_plan } : {}),
      },
    ]);
    const newKey = added?.items?.[0]?.key;

    if (!added) {
      if (save instanceof HTMLElement) save.removeAttribute('aria-busy');
      this.#setModalError('Could not save your changes. Nothing was changed.');
      return;
    }

    // 2. Remove the original by KEY — indexes shift once a line is added.
    const removed = await this.#changeKey(line.key, 0, { silent: true });

    // 3. Rush is tied to the parent by key, which just changed. Move it across.
    if (removed && rushRow && newKey) {
      try {
        const rush = this.#readLine(rushRow);
        if (rush?.key) {
          const rushAdded = await this.#addItems([
            { id: rush.id, quantity: rush.quantity, properties: { ...rush.properties, _dm_rush_for: newKey } },
          ]);
          if (rushAdded) await this.#changeKey(rush.key, 0, { silent: true });
        }
      } catch {
        // The edit itself succeeded; a stale rush link is visible and removable.
      }
    }

    if (save instanceof HTMLElement) save.removeAttribute('aria-busy');

    if (!removed) {
      this.#setModalError('Your updated item was added, but the original could not be removed. Please remove it.');
      return;
    }

    this.#dismissModal({ restoreFocus: true, focusKey: newKey || '' });
  };

  /**
   * @param {Record<string, string>} properties
   * @param {unknown} select
   * @param {string} key
   * @param {string} swatchKey
   */
  /**
   * Which colour property the line carries: build-only lines (2026-10-07) post Front Fabric
   * + _wroe_front_fabric_swatch; hub-era lines post Toy Color + _wroe_toy_color_swatch. An
   * edit always writes back to the line's own key.
   * @param {{ properties?: Record<string, string> }} line
   */
  #fabricKeys(line) {
    const legacy = Boolean(line.properties?.['Toy Color']);
    return legacy
      ? { key: 'Toy Color', swatch: '_wroe_toy_color_swatch' }
      : { key: 'Front Fabric', swatch: '_wroe_front_fabric_swatch' };
  }

  #applyChoice(properties, select, key, swatchKey) {
    if (!(select instanceof HTMLSelectElement) || select.closest('[hidden]') || !select.value) return;
    // Untouched field: keep the line's own value AND its swatch property. Rewriting the
    // swatch from the option list would swap the line's tile (C-8) for the list's image.
    if (select.value === properties[key]) return;
    properties[key] = select.value;
    const swatch = select.selectedOptions[0]?.dataset.swatch;
    // A new colour never keeps the previous colour's swatch.
    if (swatch) properties[swatchKey] = swatch;
    else delete properties[swatchKey];
  }

  /** @param {string} message */
  #setModalError(message) {
    const error = this.refs.modalError;
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  // ------------------------------------------------------------------- rush
  /**
   * A rush line is charged per parent line, so it must not outlive its parent: however the parent
   * left the cart (trash, stepper, cart page), the rush line goes with it.
   */
  #pruneOrphanRush = async () => {
    if (!this.dataset.rushVariant || this.#pruningRush) return;
    this.#pruningRush = true;
    try {
      const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      const keys = new Set(cart.items.map((item) => item.key));
      for (const item of cart.items) {
        const parent = item.properties?._dm_rush_for;
        if (parent && !keys.has(parent)) await this.#changeKey(item.key, 0);
      }
    } catch {
      // A failed check leaves the rush line visible and removable.
    } finally {
      this.#pruningRush = false;
    }
  };


  /** @param {HTMLElement} button */
  async #toggleRush(button) {
    const variantId = Number(this.dataset.rushVariant);
    if (!variantId || this.#busy) return;

    const on = button.getAttribute('aria-checked') === 'true';
    const parentKey = button.dataset.parentKey || '';
    button.setAttribute('aria-busy', 'true');

    if (on) {
      const rushRow = this.#findRushRow(parentKey);
      if (rushRow) await this.#changeLine(rushRow.dataset.dmIndex, 0);
    } else {
      await this.#addItems([
        {
          id: variantId,
          quantity: 1,
          properties: {
            _dm_rush_for: parentKey,
            [this.dataset.rushLabel || 'Rush production']: 'Yes',
          },
        },
      ]);
    }

    button.removeAttribute('aria-busy');
  }

  /** @param {string} parentKey */
  #findRushRow(parentKey) {
    for (const row of document.querySelectorAll('[data-dm-line]')) {
      const element = /** @type {HTMLElement} */ (row);
      try {
        const line = this.#readLine(element);
        if (line?.properties?._dm_rush_for === parentKey) return element;
      } catch {
        // Ignore an unparseable row rather than failing the toggle.
      }
    }
    return null;
  }

  // -------------------------------------------------------- recently viewed

  /** The handle of the product page being viewed, if any. */
  #currentHandle() {
    const product = /** @type {HTMLElement | null} */ (document.querySelector('[data-dm-product-handle]'));
    return product?.dataset.dmProductHandle || '';
  }

  /** @param {string} key */
  #readList(key) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item) : [];
    } catch {
      return [];
    }
  }

  /** @returns {Map<string, string>} */
  #readShortNames() {
    try {
      const value = JSON.parse(localStorage.getItem(SHORT_NAMES_KEY) || '{}');
      return new Map(Object.entries(value).filter(([, name]) => typeof name === 'string' && name));
    } catch {
      return new Map();
    }
  }

  /**
   * Record the product being viewed. Local to this browser; never sent anywhere.
   * A personalized PDP also marks its handle, because /products/<handle>.js does not say
   * which template a product uses. Marks are only ever added (never cleared by a later
   * standard view), so a personalized product can never regain an Add button.
   */
  #recordHistory() {
    const handle = this.#currentHandle();
    if (!handle) return;

    const personalizedPage = !!document.querySelector('.dm-hero:not(.dm-hero--standard), dm-personalizer');

    try {
      const history = [handle, ...this.#readList(HISTORY_KEY).filter((item) => item !== handle)].slice(0, HISTORY_MAX);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));

      const marked = new Set(this.#readList(PERSONALIZED_KEY));
      if (personalizedPage) marked.add(handle);
      // Bounded by the history it describes.
      const kept = history.filter((item) => marked.has(item));
      localStorage.setItem(PERSONALIZED_KEY, JSON.stringify(kept));

      const names = this.#readShortNames();
      const marker = /** @type {HTMLElement | null} */ (document.querySelector('[data-dm-product-handle]'));
      const shortName = marker?.dataset.dmProductShortName;
      if (shortName) names.set(handle, shortName);
      else names.delete(handle);
      localStorage.setItem(
        SHORT_NAMES_KEY,
        JSON.stringify(Object.fromEntries(history.filter((item) => names.has(item)).map((item) => [item, names.get(item)])))
      );
    } catch {
      // Private browsing or blocked storage — recently viewed simply does not appear.
    }
  }

  /** @type {Promise<void> | null} */
  #recentRender = null;

  #renderRecentlyViewed = () => {
    // cart:update and dialog:open can arrive together; render once at a time.
    if (!this.#recentRender) {
      this.#recentRender = this.#buildRecentlyViewed().finally(() => {
        this.#recentRender = null;
      });
    }
    return this.#recentRender;
  };

  async #buildRecentlyViewed() {
    if (this.dataset.showRecentlyViewed !== 'true') return;

    const findMount = () => {
      const mount = this.#drawerDialog?.querySelector('.cart-drawer-empty') || document.querySelector('.cart-drawer-empty');
      return mount && !mount.querySelector('.dm-recently-viewed') ? mount : null;
    };
    if (!findMount()) return;

    // Never offer the product the visitor is looking at right now.
    const current = this.#currentHandle();
    const handles = this.#readList(HISTORY_KEY)
      .filter((handle) => handle !== current)
      .slice(0, RECENT_MAX);
    if (!handles.length) return;

    const marked = new Set(this.#readList(PERSONALIZED_KEY));
    const shortNames = this.#readShortNames();
    const root = window.Shopify?.routes?.root || '/';

    const fetched = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
          return response.ok ? { handle, product: await response.json() } : null;
        } catch {
          // A deleted or unpublished product is skipped, not surfaced as an error.
          return null;
        }
      })
    );

    // The drawer may have been morphed (or filled) while the products loaded.
    const mount = findMount();
    if (!mount) return;

    const section = document.createElement('div');
    section.className = 'dm-recently-viewed';

    const heading = document.createElement('p');
    heading.className = 'dm-recently-viewed__heading';
    heading.id = 'dm-recently-viewed-heading';
    heading.textContent = this.dataset.recentlyViewedHeading || 'Recently viewed';
    section.append(heading);

    const list = document.createElement('ul');
    list.className = 'dm-recently-viewed__list';
    list.setAttribute('aria-labelledby', heading.id);

    for (const entry of fetched) {
      const product = entry?.product;
      if (!product) continue;
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant = variants.find((/** @type {any} */ v) => v.available) || variants[0];
      if (!variant) continue;

      // A personalized product cannot be added from here — it would post a line with no
      // Pet Name or colours — so it links to its page instead of offering Add. Decided
      // from data this page actually has: the visitor's own personalized-PDP marks, or
      // the product's tags (the bunny is tagged 'Customizable').
      const tags = Array.isArray(product.tags) ? product.tags : [];
      const personalized = marked.has(entry.handle) || tags.some((tag) => PERSONALIZED_TAG.test(String(tag)));

      // Quick Add only when there is nothing to choose. A product with real options
      // (size, colour, length) would otherwise get whichever variant happens to be first
      // in stock — a size the customer never picked — so it links to its page instead.
      const options = Array.isArray(product.options) ? product.options : [];
      const singleChoice =
        variants.length === 1 ||
        (options.length > 0 && options.every((/** @type {any} */ option) => (option?.values?.length ?? 0) <= 1));

      // History saved before a product's short name was seen: the title up to its first comma.
      const name = shortNames.get(entry.handle) || String(product.title).split(',')[0];

      const item = document.createElement('li');
      item.className = 'dm-recently-viewed__item';

      const media = document.createElement('a');
      media.className = 'dm-recently-viewed__media';
      media.href = product.url;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      if (product.featured_image) {
        const img = document.createElement('img');
        img.className = 'dm-recently-viewed__image';
        img.src = this.#sizedImage(product.featured_image, 240);
        img.alt = '';
        img.width = 120;
        img.height = 120;
        img.loading = 'lazy';
        media.append(img);
      }
      item.append(media);

      const link = document.createElement('a');
      link.className = 'dm-recently-viewed__title';
      link.href = product.url;
      link.textContent = name;

      // A single variant's price, or — when the customer still has to choose and prices
      // differ — the product's own lowest price as "From", never one arbitrary variant's.
      const price = document.createElement('p');
      price.className = 'dm-recently-viewed__price';
      const priceVaries = !singleChoice && product.price_varies === true && Number.isFinite(product.price_min);
      price.textContent = priceVaries ? `From ${this.#money(product.price_min)}` : this.#money(variant.price);

      item.append(link, price);

      if (personalized) {
        const personalize = document.createElement('a');
        personalize.className = 'dm-recently-viewed__add dm-recently-viewed__add--link';
        personalize.href = product.url;
        personalize.textContent = 'Personalize';
        personalize.setAttribute('aria-label', `Personalize ${name}`);
        item.append(personalize);
      } else if (!singleChoice) {
        if (product.available !== false && variants.some((/** @type {any} */ v) => v.available)) {
          const choose = document.createElement('a');
          choose.className = 'dm-recently-viewed__add dm-recently-viewed__add--link';
          choose.href = product.url;
          choose.textContent = 'Choose options';
          choose.setAttribute('aria-label', `Choose options for ${name}`);
          item.append(choose);
        }
      } else if (variant.available) {
        const add = document.createElement('button');
        add.className = 'dm-recently-viewed__add';
        add.type = 'button';
        add.textContent = 'Add';
        add.setAttribute('aria-label', `Add ${name} to cart`);
        add.dataset.dmRecentAdd = String(variant.id);
        item.append(add);
      }

      list.append(item);
    }

    if (!list.childElementCount) return;
    section.append(list);
    mount.append(section);
  }

  /**
   * Ask the CDN for a thumbnail-sized file instead of the original upload.
   *
   * @param {string} src
   * @param {number} width
   */
  #sizedImage(src, width) {
    if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(src) || /[?&]width=/.test(src)) return src;
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  }

  /** @param {HTMLElement} button */
  async #addRecent(button) {
    const id = Number(button.dataset.dmRecentAdd);
    if (!id || this.#busy || button.getAttribute('aria-pressed') === 'true') return;

    button.setAttribute('aria-pressed', 'true');
    button.textContent = 'Added';
    const added = await this.#addItems([{ id, quantity: 1 }]);
    if (!added && button.isConnected) {
      button.setAttribute('aria-pressed', 'false');
      button.textContent = 'Add';
      return;
    }

    // The empty state (and this button with it) is replaced by the filled drawer; put
    // focus on the line just added rather than leaving it on <body>. If the button is
    // still there and focused, the drawer has not re-rendered: focus stays put.
    const row = await this.#waitForLine(added?.items?.[0]?.key);
    if (this.#focusLost()) this.#focusLineTarget(row);
  }

  // -------------------------------------------------------------- cart calls

  /**
   * Remove or re-quantity a line by its 1-based index.
   *
   * @param {string | undefined} index
   * @param {number} quantity
   * @param {{ silent?: boolean }} [options]
   * @returns {Promise<any>} the response payload on success, otherwise null
   */
  async #changeLine(index, quantity, options = {}) {
    if (!index) return false;

    const body = JSON.stringify({
      line: Number(index),
      quantity,
      sections: this.#sections(),
      sections_url: window.location.pathname,
    });

    return this.#send(window.Theme?.routes?.cart_change_url || '/cart/change.js', body, options);
  }

  /**
   * Remove or re-quantity a line by its key. Stable across adds, unlike the index.
   *
   * @param {string | undefined} key
   * @param {number} quantity
   * @param {{ silent?: boolean }} [options]
   */
  async #changeKey(key, quantity, options = {}) {
    if (!key) return null;

    const body = JSON.stringify({
      id: key,
      quantity,
      sections: this.#sections(),
      sections_url: window.location.pathname,
    });

    return this.#send(window.Theme?.routes?.cart_change_url || '/cart/change.js', body, options);
  }

  /**
   * @param {object[]} items
   * @returns {Promise<any>} the response payload on success, otherwise null
   */
  async #addItems(items) {
    const body = JSON.stringify({
      items,
      sections: this.#sections(),
      sections_url: window.location.pathname,
    });

    return this.#send(window.Theme?.routes?.cart_add_url || '/cart/add.js', body);
  }

  /**
   * @param {string} url
   * @param {string} body
   * @param {{ silent?: boolean }} [options]
   * @returns {Promise<any>} the response payload on success, otherwise null
   */
  async #send(url, body, options = {}) {
    this.#busy = true;
    try {
      const response = await fetch(url, fetchConfig('json', { body }));
      const payload = await response.json();
      // /cart/add.js reports failure as `status`; /cart/change.js reports it as
      // `errors`. Treat either as a failure so a caller never reports a false save.
      if (!response.ok || payload.errors || (payload.status && !payload.sections)) return null;

      await this.#applySections(payload);
      if (!options.silent) this.dispatchEvent(new CustomEvent('dm:cart-changed', { bubbles: true }));
      return payload;
    } catch {
      return null;
    } finally {
      this.#busy = false;
      this.#setCheckoutLoading(false);
    }
  }

  /**
   * Re-render every cart section the response carried, the way the theme does, then tell
   * the rest of the page the real item count. /cart/add.js answers with the added lines
   * only, so the count comes from /cart.js; without it cart-icon.js resets the header
   * bubble to 0 while the cart holds items.
   */
  async #applySections(/** @type {any} */ payload) {
    const sections = payload?.sections;
    if (!sections) return;

    for (const [id, html] of Object.entries(sections)) {
      if (typeof html === 'string') morphSection(id, html, 'hydration');
    }
    // Immediately, from what was just rendered, so a filled drawer never flashes the
    // centred empty-state layout while the count is fetched.
    this.#syncEmptyState();

    const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    const itemCount = [cart?.item_count, payload.item_count, this.#renderedCount()].find(
      (value) => typeof value === 'number' && Number.isFinite(value)
    );
    if (typeof itemCount === 'number') this.#syncEmptyState(itemCount === 0);

    const first = Object.keys(sections)[0] || '';
    document.dispatchEvent(
      new CartUpdateEvent(cart || payload, first, {
        source: 'dm-cart-drawer',
        sections,
        ...(typeof itemCount === 'number' ? { itemCount } : {}),
      })
    );
  }

  /** Last resort for the bubble: the "(n)" the drawer heading was just rendered with. */
  #renderedCount() {
    const text = this.#drawerDialog?.querySelector('.cart-drawer__heading-count')?.textContent || '';
    const match = text.match(/\d+/);
    return match ? Number(match[0]) : undefined;
  }

  #queueEmptySync = () => {
    window.cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = window.requestAnimationFrame(() => {
      this.#syncEmptyState();
      // A morph can move or remove the Checkout CTA while the undo toast is up.
      this.#positionToast();
      // A morph of the empty drawer (section re-render, discount update) drops the
      // client-built Recently viewed list; put it back. Idempotent and loop-free: it only
      // appends when the list is missing and there is something to show.
      const mount = this.#drawerDialog?.querySelector('.cart-drawer-empty');
      if (mount && !mount.querySelector('.dm-recently-viewed')) this.#renderRecentlyViewed();
    });
  };

  /**
   * The `cart-drawer--empty` modifier and the dialog's accessible name live on the
   * <dialog>, which no cart morph reaches. Without this the class set at page load sticks
   * after the first add and centres every line card (cart-empty-class-sticks).
   *
   * @param {boolean} [empty] known emptiness (from item_count); read from the DOM otherwise
   */
  #syncEmptyState(empty) {
    const dialog = this.#drawerDialog;
    if (!dialog) return;

    const isEmpty =
      typeof empty === 'boolean'
        ? empty
        : !!dialog.querySelector('.cart-drawer-empty') && !dialog.querySelector('[data-dm-line], .cart-items__table-row');
    if (dialog.classList.contains('cart-drawer--empty') !== isEmpty) {
      dialog.classList.toggle('cart-drawer--empty', isEmpty);
    }

    const labelId = isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading';
    if (document.getElementById(labelId) && dialog.getAttribute('aria-labelledby') !== labelId) {
      dialog.setAttribute('aria-labelledby', labelId);
    }
  }

  #sections() {
    const ids = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      const id = /** @type {HTMLElement} */ (element).dataset.sectionId;
      if (id) ids.add(id);
    }
    return Array.from(ids).join(',');
  }

  /** @param {boolean} loading */
  #setCheckoutLoading(loading) {
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (dialog instanceof HTMLElement) {
      if (loading) dialog.dataset.dmCheckout = 'loading';
      else delete dialog.dataset.dmCheckout;
    }
  }

  /** @param {number} cents */
  #money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    } catch {
      return '';
    }
  }
}

if (!customElements.get('dm-cart-drawer')) {
  customElements.define('dm-cart-drawer', DmCartDrawer);
}
